import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Box,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  NumberInput,
  NumberInputField,
  Divider,
  useColorModeValue,
  Avatar,
  IconButton,
  Tooltip,
  InputGroup,
  InputRightElement,
} from '@chakra-ui/react';
import { FaDollarSign, FaTrash, FaInfoCircle, FaUndo, FaTimes } from 'react-icons/fa';
import { useModernToast } from './ModernToast';
import { useMyLeagueStore } from '../store/myLeagueStore';
import { getTeamInfoById } from '../utils/getTeamInfo';
import { useUserContext } from '../contexts/UserContext';
import { formatSalary } from '../utils/formatMoney';
import { formatPstDateTime } from '../lib/pstTime';
import TeamLogo from './TeamLogo';
import { useTeam } from '../hooks/useTeam';
import { useTeamRoster } from '../hooks/useTeamRoster';
import StripePaymentForm from './StripePreApproval';
import apiClient from '../lib/api/apiClient';
import { useUserStore } from '../store/userStore';
import { formatPositionsList } from '../utils/formatPositionsList';
import { httpsPlayerImageUrl } from '../utils/stringUtils';

interface Player {
  id: number;
  primaryPosition?: string[];
  secondaryPosition?: string[];
  tertiaryPosition?: string[];
  rwPlayer: {
    id: number;
    first_name: string;
    last_name: string;
    position_1: string;
    position_2?: string;
    position_3?: string;
    image?: string;
    bats?: string;
    throws?: string;
    seasonPositions?: {
      primaryPosition?: string[] | null;
      secondaryPosition?: string[] | null;
      tertiaryPosition?: string[] | null;
    } | null;
  };
  playerContract?: {
    current_salary: number;
    total_value?: number;
    contractYears?: { year: number; salary: number }[];
  };
  status?: string;
  level?: string;
  team_id?: number;
  dealData?: {
    bids?: number;
    expires?: string;
    offers?: {
      offer: {
        yearlySalaries: number[];
      };
    }[];
  };
}

function formatPlayerPositionsLine(player: Player): string {
  const sp = player.rwPlayer?.seasonPositions;
  return formatPositionsList({
    primaryPosition: (player.primaryPosition ?? sp?.primaryPosition) ?? undefined,
    secondaryPosition: (player.secondaryPosition ?? sp?.secondaryPosition) ?? undefined,
    tertiaryPosition: (player.tertiaryPosition ?? sp?.tertiaryPosition) ?? undefined,
  });
}

interface SignFreeAgentModalProps {
  isOpen: boolean;
  onClose: () => void;
  player: Player;
  level: 'MLB' | 'AAA';
  refetch?: () => void;
  existingDealId?: number | null;
  initialYearlySalaries?: number[];
  viewOnly?: boolean;
  winningTeamId?: number;
}

const SignFreeAgentModal: React.FC<SignFreeAgentModalProps> = ({
  isOpen,
  onClose,
  player,
  level,
  refetch,
  existingDealId,
  initialYearlySalaries,
  viewOnly = false,
  winningTeamId,
}) => {

  // TODO: This will eventually come from database configuration
  const minSalary = 780000;
  const maxSalary = 100000000;
  const maxYears = 7;
  const initialYears = 3;

  const { user } = useUserStore();

  const { systemSettings } = useUserContext();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFetchingDeal, setIsFetchingDeal] = useState(false);
  const [yearlySalaries, setYearlySalaries] = useState<number[]>(initialYearlySalaries || Array(initialYears).fill(0));
  const [debouncedYearlySalaries, setDebouncedYearlySalaries] = useState<number[]>(yearlySalaries);
  const [error, setError] = useState<string | null>(null);

  // Debounce yearlySalaries for validation messages
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedYearlySalaries(yearlySalaries);
    }, 1000);

    return () => {
      clearTimeout(handler);
    };
  }, [yearlySalaries]);

  const toast = useModernToast();
  const myLeague = useMyLeagueStore(state => state.myLeague);
  const { myTeam } = useUserContext();

  const [submitterInfo, setSubmitterInfo] = useState<{
    userName: string;
    teamName: string;
    submittedDate: string;
  } | null>(null);

  const [displayedDealId, setDisplayedDealId] = useState<number | null>(null);

  // Fetch existing deal if ID is provided OR look it up via status endpoint
  useEffect(() => {
    const fetchDealInfo = async () => {
      // If we have an explicit ID passed in props, use it
      if (existingDealId && !viewOnly) {
        setIsFetchingDeal(true);
        try {
          const extendedResponse = await apiClient.get<any>(`/deals/getExtendedDealInfo/${myTeam}/${player.id}`);
          const dealFn = extendedResponse.data;
          console.log("Found existing deal via extended info:", dealFn);
          setDisplayedDealId(dealFn.dealData.id);

          handleDealData(dealFn.dealData);
        } catch (err) {
          console.error("Error fetching existing deal:", err);
          setError("Failed to load existing offer details.");
        } finally {
          setIsFetchingDeal(false);
        }
      } else if (viewOnly && winningTeamId && myLeague?.id && player.id) {
        // If viewing a past transaction for a specific winning team
        setIsFetchingDeal(true);
        try {
          // Use the specific status endpoint to get that team's deal info
          const statusResponse = await apiClient.get<any>(`/deals/free-agent-status/${myLeague.id}/${player.id}/${winningTeamId}`);
          const statusData = statusResponse.data;

          if (statusData) {
            setDisplayedDealId(statusData.id);
            handleDealData(statusData);

            // Also try to get extended info for submitter details if available
            // For the winning deal, the dealData might already have some info, or we can try fetching extended info
            if (statusData.id) {
              try {
                // We might need a way to get the submitter info for a specific deal ID or team
                // For now, let's see if we can get it via the extended info endpoint but for the winning team
                // The current endpoint /deals/getExtendedDealInfo/:teamId/:playerId uses the teamId param
                if (winningTeamId == myTeam) {
                  const extendedResponse = await apiClient.get<any>(`/deals/getExtendedDealInfo/${winningTeamId}/${player.id}`);
                  const extendedData = extendedResponse.data;
                  if (extendedData) {
                    setSubmitterInfo({
                      userName: extendedData.userName || 'Unknown User',
                      teamName: extendedData.teamName || 'Unknown Team',
                      submittedDate: extendedData.submittedDate ? formatPstDateTime(extendedData.submittedDate, true) : 'Unknown Date',
                    });
                  }
                }

              } catch (e) {
                console.log("Could not fetch extended info for winning team:", e);
              }
            }
          }
        } catch (err) {
          console.error("Error fetching winning deal info:", err);
          setError("Failed to load winning offer details.");
        } finally {
          setIsFetchingDeal(false);
        }
      } else {
        // If no ID provided, try to find one via extended info endpoint (for current user)
        if (myLeague?.id && player.id) {
          try {
            // We can use this to get the deal ID if we are updating or viewing
            const extendedResponse = await apiClient.get<any>(`/deals/getExtendedDealInfo/${myTeam}/${player.id}`);
            const extendedData = extendedResponse.data;

            if (extendedData) {
              // Set submitter info right away since we have it
              setSubmitterInfo({
                userName: extendedData.userName || 'Unknown User',
                teamName: extendedData.teamName || 'Unknown Team',
                submittedDate: extendedData.submittedDate ? formatPstDateTime(extendedData.submittedDate, true) : 'Unknown Date',
              });

              // If we have a deal ID from this, use it
              if (extendedData.dealData.id) {
                console.log("Found existing deal via extended info:", extendedData.dealData.id);
                setDisplayedDealId(extendedData.dealData.id);

                // Use the extended data directly as it contains the relevant deal info for this user
                handleDealData(extendedData.dealData);
              }
            }
          } catch (e) {
            // It's fine if this fails (e.g. 404 no deal found), just means new offer
            console.log("No existing deal extended info found or error:", e);
          }
        }
      }
    };

    const handleDealData = async (dealFn: any) => {
      let finalDealFn = dealFn;

      // Prioritize the offer from player.dealData.offers if we are not in viewOnly mode
      // and we are looking at our own team's offer. This ensures we display the most
      // up-to-date offer when a user has made multiple updates.
      if (!viewOnly && (!winningTeamId || winningTeamId === myTeam) && player.dealData?.offers) {
        // Use findLast or spread and reverse to get the most recent offer in the array
        const myLatestOffer = [...player.dealData.offers].reverse().find((o: any) => Number(o.team) === Number(myTeam));
        if (myLatestOffer && myLatestOffer.offer?.yearlySalaries) {
          finalDealFn = myLatestOffer;
        }
      }

      if (finalDealFn) {
        // Check for contract details in different possible locations
        let salaries = [];

        if (finalDealFn.contract_details && finalDealFn.contract_details.yearlySalaries) {
          salaries = finalDealFn.contract_details.yearlySalaries;
        } else if (finalDealFn.offer && finalDealFn.offer.yearlySalaries) {
          salaries = finalDealFn.offer.yearlySalaries;
        }

        // Ensure it's an array and pad/trim as needed, or just set it
        // We'll trust the stored salaries but ensure it's not empty
        if (Array.isArray(salaries) && salaries.length > 0) {
          setYearlySalaries(salaries);
        }
      }

      // Fetch extended deal info only if we haven't already (i.e. if existingDealId was passed)
      // or if we want to ensure we have it.
      // In the case where we discovered the deal via extended info, we already set submitterInfo above.
      // But if existingDealId was passed, we still need to fetch it.
      const dealIdToUse = dealFn ? dealFn.id : existingDealId;

      if (existingDealId && dealIdToUse && (!viewOnly || viewOnly) && !submitterInfo) {
        try {
          const extendedResponse = await apiClient.get<any>(`/deals/getExtendedDealInfo/${myTeam}/${player.id}`);
          const extendedData = extendedResponse.data;
          if (extendedData) {
            setSubmitterInfo({
              userName: extendedData?.userName || 'Unknown User',
              teamName: extendedData?.teamName || 'Unknown Team',
              submittedDate: extendedData.submittedDate ? formatPstDateTime(extendedData.submittedDate, true) : 'Unknown Date',
            });
          }
        } catch (extErr) {
          console.error("Error fetching extended deal info:", extErr);
          // Don't block the main functionality if this fails
        }
      }
    }

    if (isOpen) {
      if (existingDealId || (viewOnly && winningTeamId)) {
        // If we have an ID or are viewing a specific team's deal, fetch that
        fetchDealInfo();
      } else {
        // If no ID, reset form BUT check for existing status (as requested)
        setYearlySalaries(Array(initialYears).fill(0));
        setSubmitterInfo(null);
        setDisplayedDealId(null);

        // Check status to see if there IS a deal we should know about (e.g. user previously offered)
        fetchDealInfo();
      }
    }
  }, [isOpen, existingDealId, viewOnly, winningTeamId, myLeague?.id, player.id, myTeam]);


  // Get current team's payroll and salary cap for validation
  const { team: currentTeamData } = useTeam(myTeam || undefined);
  const { total_payroll: currentPayroll } = useTeamRoster((myTeam ?? 1) as number);

  // Stripe pre-approval state for salary cap exceedance
  const [isPreApprovalOpen, setIsPreApprovalOpen] = useState(false);
  const [preApprovalAmount, setPreApprovalAmount] = useState(0);
  const [pendingOfferSubmission, setPendingOfferSubmission] = useState<(() => Promise<void>) | null>(null);
  const [paymentIntentId, setPaymentIntentId] = useState<string | null>(null);

  // Color mode values
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.800', 'white');
  const secondaryText = useColorModeValue('gray.600', 'gray.400');
  const accentColor = useColorModeValue('blue.500', 'blue.400');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  // Get team info for the user's team or the winning team if in view mode
  const teamIdDisplay = (viewOnly && winningTeamId) ? winningTeamId : myTeam;
  const teamInfoToDisplay = getTeamInfoById(teamIdDisplay, myLeague);
  const myTeamInfo = getTeamInfoById(myTeam, myLeague); // Keep this for logic that depends on "my" team

  // Get current year
  const currentYear = new Date().getFullYear();

  // Get current year's salary from contract
  const currentYearSalary = player.playerContract?.contractYears?.find(
    year => year.year === currentYear
  )?.salary || player.playerContract?.current_salary || 0;

  // Get total contract value
  const totalContractValue = player.playerContract?.total_value ||
    player.playerContract?.contractYears?.reduce((sum, year) => sum + year.salary, 0) || 0;

  // Calculate remaining contract years (including this year)
  const remainingContractYears = (() => {
    if (!player.playerContract?.contractYears || player.playerContract.contractYears.length === 0) {
      return 0;
    }

    // Count years from current year onwards that have salaries
    let count = 0;
    for (const contractYear of player.playerContract.contractYears) {
      if (contractYear.year >= currentYear && contractYear.salary > 0) {
        count++;
      }
    }

    return count;
  })();

  // Calculate total bid
  const totalBid = yearlySalaries.reduce((sum, salary) => sum + salary, 0);

  // Check if form is valid - no gaps allowed, must be consecutive, year-to-year salary changes within 50%-200%
  const isFormValid = (() => {
    // Must have at least one valid year
    if (!yearlySalaries.some((salary: number) => salary >= minSalary)) {
      return false;
    }

    // Check that all years with any salary > 0 have valid salaries within min/max range
    for (let i = 0; i < yearlySalaries.length; i++) {
      if (yearlySalaries[i] > 0 && (yearlySalaries[i] < minSalary || yearlySalaries[i] > maxSalary)) {
        return false;
      }
    }

    // Check for gaps - no zero entries between non-zero entries
    let foundNonZero = false;
    for (let i = 0; i < yearlySalaries.length; i++) {
      if (yearlySalaries[i] >= minSalary) {
        foundNonZero = true;
      } else if (yearlySalaries[i] === 0 && foundNonZero) {
        // Found a zero after we've seen a non-zero - check if there are more non-zeros after
        for (let j = i + 1; j < yearlySalaries.length; j++) {
          if (yearlySalaries[j] >= minSalary) {
            return false; // Gap found - zero between non-zeros
          }
        }
      }
    }

    // Check year-to-year salary changes (50% to 200% of previous year)
    for (let i = 1; i < yearlySalaries.length; i++) {
      const currentSalary = yearlySalaries[i];
      const previousSalary = yearlySalaries[i - 1];

      // Only check if both years have valid salaries
      if (currentSalary >= minSalary && previousSalary >= minSalary) {
        const minAllowed = previousSalary * 0.5;
        const maxAllowed = previousSalary * 2.0;

        if (currentSalary < minAllowed || currentSalary > maxAllowed) {
          return false;
        }
      }
    }

    // If updating an offer, the new total value must be >= the previous total value
    if (initialYearlySalaries && initialYearlySalaries.length > 0) {
      const initialTotal = initialYearlySalaries.reduce((sum, salary) => sum + salary, 0);
      const currentTotal = yearlySalaries.reduce((sum, salary) => sum + salary, 0);
      // We allow equal value, so only return false if STRICTLY less
      if (currentTotal < initialTotal) {
        return false;
      }
    }

    return true;
  })();

  // Calculate info for display
  const initialTotalValue = initialYearlySalaries ? initialYearlySalaries.reduce((sum, s) => sum + s, 0) : 0;
  const isValueTooLow = initialTotalValue > 0 && totalBid < initialTotalValue;

  // Check if we can add another year (all current years must be valid, no gaps, and under max years)
  const canAddYear = (() => {
    // Can't add more years if we're already at the maximum
    if (yearlySalaries.length >= maxYears) {
      return false;
    }

    // Must have at least one valid year
    if (!yearlySalaries.some((salary: number) => salary >= minSalary)) {
      return false;
    }

    // Check that all years with any salary > 0 have valid salaries >= minSalary
    for (let i = 0; i < yearlySalaries.length; i++) {
      if (yearlySalaries[i] > 0 && yearlySalaries[i] < minSalary) {
        return false;
      }
    }

    // Check for gaps - no zero entries between non-zero entries
    let foundNonZero = false;
    for (let i = 0; i < yearlySalaries.length; i++) {
      if (yearlySalaries[i] >= minSalary) {
        foundNonZero = true;
      } else if (yearlySalaries[i] === 0 && foundNonZero) {
        // Found a zero after we've seen a non-zero - check if there are more non-zeros after
        for (let j = i + 1; j < yearlySalaries.length; j++) {
          if (yearlySalaries[j] >= minSalary) {
            return false; // Gap found - zero between non-zeros
          }
        }
      }
    }

    return true;
  })();

  // Handle salary change for a specific year
  const handleSalaryChange = (yearIndex: number, value: string) => {
    const newYearlySalaries = [...yearlySalaries];
    const numValue = Number(value.replace(/,/g, '').replace(/\$/g, ''));
    newYearlySalaries[yearIndex] = numValue || 0;
    setYearlySalaries(newYearlySalaries);
  };

  // Add a new year
  const handleAddYear = () => {
    setYearlySalaries([...yearlySalaries, 0]);
  };

  // Handle removing the last year
  const handleRemoveLastYear = () => {
    if (yearlySalaries.length > 1) {
      const newYearlySalaries = [...yearlySalaries];
      newYearlySalaries.pop();
      setYearlySalaries(newYearlySalaries);
    }
  };

  // Helper to calculate offer metrics
  const calculateOfferMetrics = (salaries: number[]) => {
    const yearsCount = salaries.filter(s => s >= minSalary).length;
    const validTotal = salaries.reduce((sum, s) => s >= minSalary ? sum + s : sum, 0);
    const aav = yearsCount > 0 ? validTotal / yearsCount : 0;
    const offerScore = (aav * 0.85) + (validTotal * 0.15);
    return { yearsCount, validTotal, offerScore };
  };

  // Calculate top offer score
  const topOfferScore = (() => {
    if (!player.dealData?.offers || player.dealData.offers.length === 0) return 0;

    let maxScore = 0;
    player.dealData.offers.forEach(o => {
      if (o.offer?.yearlySalaries) {
        const { offerScore } = calculateOfferMetrics(o.offer.yearlySalaries);
        if (offerScore > maxScore) maxScore = offerScore;
      }
    });
    return maxScore;
  })();

  // Calculate current user's offer metrics
  const { offerScore: yourOfferScore } = calculateOfferMetrics(yearlySalaries);

  // Check if free agent offer would exceed salary cap and calculate excess amount
  const checkSalaryCapExceedance = async (firstYearSalary: number): Promise<{ exceeds: boolean; excessAmount: number }> => {
    if (!myTeam || !currentTeamData?.salary_cap || !currentPayroll || !myLeague?.id) {
      return { exceeds: false, excessAmount: 0 };
    }

    // Get the max salary change from all outstanding deals for this team
    const response = await apiClient.get<{ positiveChange: boolean; positiveChangeAmount: number }>(
      `/deals/getMaxSalaryChangeFromDealsForTeam/${myTeam}/${myLeague.id}`
    );
    const { positiveChange, positiveChangeAmount } = response.data;

    // Calculate the worst-case payroll scenario:
    // - If outstanding deals have a positive max change (increasing payroll), add it
    // - Then add this player's first year salary on top
    const outstandingDealsImpact = positiveChange ? positiveChangeAmount : 0;
    const newPayroll = currentPayroll + outstandingDealsImpact + firstYearSalary;

    // Salary cap is in millions, convert to actual dollars
    const salaryCapInDollars = Number(currentTeamData.salary_cap) * 1000000;

    // Check if new payroll exceeds salary cap
    if (newPayroll > salaryCapInDollars) {
      const excessAmount = newPayroll - salaryCapInDollars;
      return { exceeds: true, excessAmount };
    }

    return { exceeds: false, excessAmount: 0 };
  };

  // Actual offer submission logic (extracted for reuse)
  // Accept optional paymentIntentId parameter to avoid race condition with state updates
  const submitOfferInternal = async (overridePaymentIntentId?: string | null) => {
    if (!myTeam || !player.id) {
      setError('Missing team or player information');
      return;
    }

    if (!isFormValid) {
      setError('Please ensure all years have salaries of at least $780,000 and are consecutive.');
      return;
    }

    if (!myLeague?.id) {
      setError('League information not available');
      return;
    }

    // Use overridePaymentIntentId if provided, otherwise fall back to state
    const effectivePaymentIntentId = overridePaymentIntentId !== undefined ? overridePaymentIntentId : paymentIntentId;

    setIsSubmitting(true);
    setError(null);

    try {
      // Create contract details from yearly salaries
      const yearsCount = yearlySalaries.filter(salary => salary >= minSalary).length;
      const validTotal = yearlySalaries.reduce((sum, s) => s >= minSalary ? sum + s : sum, 0);
      const aav = yearsCount > 0 ? validTotal / yearsCount : 0;
      const offerScore = (aav * 0.85) + (validTotal * 0.15);

      const contractDetails = {
        salary: yearlySalaries[0], // First year salary
        years: yearsCount, // Count of valid years
        contractType: 'free_agent',
        yearlySalaries: yearlySalaries.filter(salary => salary >= minSalary), // Only include valid years
        offerScore
      };

      // Make API call directly to include paymentIntentId in the request
      const requestBody: any = {
        teamId: myTeam,
        leagueId: myLeague.id,
        contractDetails
      };

      // Add paymentIntentId to request if available (for linking)
      if (effectivePaymentIntentId) {
        requestBody.paymentIntentId = effectivePaymentIntentId;
      }

      console.log('Submitting free agent offer with paymentIntentId:', effectivePaymentIntentId);
      const response = await apiClient.post(`/deals/free_agent_offer/${player.id}`, requestBody);
      const newDeal = response.data;
      console.log('Free agent offer response:', newDeal);

      if (newDeal) {
        // For new free agent offers, update PaymentIntent metadata with dealId after creation
        // This creates a two-way link: deal has paymentIntentId, PaymentIntent has dealId
        // Note: Backend must support POST /store/update-payment-intent-metadata endpoint
        // Alternative: Backend could handle this automatically when creating deals with paymentIntentId
        if (effectivePaymentIntentId && newDeal.id) {
          try {
            console.log('Updating PaymentIntent metadata for new free agent deal:', { paymentIntentId: effectivePaymentIntentId, dealId: newDeal.id });
            await apiClient.post('/store/update-payment-intent-metadata', {
              paymentIntentId: effectivePaymentIntentId,
              metadata: { dealId: String(newDeal.id) }
            });
            console.log('Successfully linked PaymentIntent to new free agent deal:', { paymentIntentId: effectivePaymentIntentId, dealId: newDeal.id });
          } catch (error) {
            console.error('Failed to update PaymentIntent metadata with dealId:', error);
            // Don't fail the deal creation if metadata update fails - the deal still has paymentIntentId
          }
        } else {
          console.warn('Skipping PaymentIntent metadata update:', {
            hasPaymentIntentId: !!effectivePaymentIntentId,
            hasDealId: !!newDeal.id,
            paymentIntentId: effectivePaymentIntentId,
            dealId: newDeal.id
          });
        }

        toast.success('Success', `Offer submitted for ${player.rwPlayer.first_name} ${player.rwPlayer.last_name} to ${myTeamInfo.name}!`);
        setPaymentIntentId(null); // Clear after successful linking

        // Refresh the free agents data to show updated offer count
        if (refetch) {
          refetch();
        }
        onClose();
      } else {
        setError('Failed to create offer - no deal returned');
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to submit offer. Please try again.';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSignPlayer = async () => {
    if (!myTeam || !player.id) {
      setError('Missing team or player information');
      return;
    }

    if (!isFormValid) {
      setError('Please ensure all years have salaries of at least $780,000 and are consecutive.');
      return;
    }

    // Get first year salary (the one that affects current year payroll)
    const firstYearSalary = yearlySalaries[0] >= minSalary ? yearlySalaries[0] : 0;

    // Check if offer would exceed salary cap
    const { exceeds, excessAmount } = await checkSalaryCapExceedance(firstYearSalary);

    if (exceeds) {
      // Store the submission function and show pre-approval modal
      setPendingOfferSubmission(() => submitOfferInternal);
      setPreApprovalAmount(excessAmount);
      setIsPreApprovalOpen(true);
      return;
    }

    // No salary cap issue, proceed with submission
    await submitOfferInternal();
  };

  // Handle pre-approval success
  const handlePreApprovalSuccess = async (result: any) => {
    const { paymentIntent, paymentIntentId: piId } = result;
    console.log('Pre-approval successful, submitting offer...', { paymentIntent, paymentIntentId: piId });

    // Store paymentIntentId for linking with deal
    if (piId) {
      setPaymentIntentId(piId);

      // Update purchase status to pending
      try {
        await apiClient.put(`/purchases/${piId}/status/pending`);
        console.log('Purchase status updated to pending:', piId);
      } catch (error) {
        console.error('Error updating purchase status to pending:', error);
        // Don't fail the entire flow if purchase update fails
      }
    }

    // Close the pre-approval modal
    setIsPreApprovalOpen(false);

    // Proceed with offer submission - pass paymentIntentId directly to avoid race condition
    if (pendingOfferSubmission) {
      try {
        console.log('Calling pending offer submission with paymentIntentId:', piId);
        // Pass paymentIntentId directly to avoid race condition with state update
        await submitOfferInternal(piId);
        console.log('Offer submitted successfully after pre-approval');
        setPendingOfferSubmission(null);
      } catch (error) {
        console.error('Error submitting offer after pre-approval:', error);
        // Error is already handled in submitOfferInternal, but we should clear the pending submission
        setPendingOfferSubmission(null);
      }
    } else {
      console.warn('No pending offer submission found after pre-approval success');
      toast.error('Error', 'Offer submission failed - no pending submission found');
    }
  };

  // Handle pre-approval failure/close
  const handlePreApprovalClose = async () => {
    // If paymentIntentId exists, a purchase was created and should be deleted
    if (paymentIntentId) {
      try {
        await apiClient.delete(`/purchases/${paymentIntentId}`);
        console.log('Purchase deleted:', paymentIntentId);
      } catch (error) {
        console.error('Error deleting purchase:', error);
        // Don't show error to user, just log it
      }
    }

    setIsPreApprovalOpen(false);
    setPendingOfferSubmission(null);
    setPaymentIntentId(null); // Clear paymentIntentId on close
    toast.error('Error', 'Payment authorization is required to proceed with this offer. Please adjust your contract offer to stay within your salary cap or authorize the payment.');
  };

  const handleClose = () => {
    setError(null);
    setYearlySalaries(Array(initialYears).fill(0));
    setIsPreApprovalOpen(false);
    setPendingOfferSubmission(null);
    setPaymentIntentId(null); // Clear paymentIntentId on close
    onClose();
  };

  const positions = formatPlayerPositionsLine(player);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg" isCentered>
      <ModalOverlay />
      <ModalContent bg={bgColor} color={textColor} border={`1px solid ${borderColor}`} maxH="90vh">
        <ModalHeader>
          <HStack spacing={3}>
            <FaDollarSign color={accentColor} />
            <Text>
              {viewOnly
                ? `Winning Offer for ${player.rwPlayer.first_name} ${player.rwPlayer.last_name}`
                : existingDealId || initialYearlySalaries
                  ? 'Editing'
                  : myTeamInfo.name + ' Offer for ' + player.rwPlayer.first_name + ' ' + player.rwPlayer.last_name}
            </Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody overflowY="auto" onWheel={(e) => e.stopPropagation()}>
          {isFetchingDeal ? (
            <Box display="flex" justifyContent="center" alignItems="center" height="200px">
              <Text>Loading existing offer details...</Text>
            </Box>
          ) : (
            <VStack spacing={6} align="stretch">
              {/* Two Column Layout: Player -> Team */}
              <Box display="flex" justifyContent="center">
                <HStack spacing={6} align="center">
                  {/* Left Column: Player Information */}
                  <VStack spacing={1} align="center" minW="100px">
                    <Avatar
                      size="lg"
                      src={httpsPlayerImageUrl(player.rwPlayer.image)}
                      name={`${player.rwPlayer.first_name} ${player.rwPlayer.last_name}`}
                      ignoreFallback={false}
                    />
                    <Text fontSize="lg" fontWeight="bold" textAlign="center">
                      {player.rwPlayer.first_name} {player.rwPlayer.last_name}
                    </Text>
                    <Text fontSize="sm" color={secondaryText} textAlign="center">
                      {positions}
                    </Text>
                  </VStack>

                  {/* Center: Arrow */}
                  <Box>
                    <Text fontSize="2xl" color={accentColor} fontWeight="bold">
                      →
                    </Text>
                  </Box>

                  {/* Right Column: Team Information */}
                  <VStack spacing={1} align="center" minW="100px">
                    <TeamLogo
                      team={{
                        name: teamInfoToDisplay.name,
                        logo: teamInfoToDisplay.logo,
                        color_primary: teamInfoToDisplay.color_primary,
                        color_secondary: teamInfoToDisplay.color_secondary,
                        color_text: teamInfoToDisplay.color_text
                      }}
                      size="lg"
                    />
                    <Text fontSize="md" fontWeight="bold" textAlign="center" color={textColor} mt={1}>
                      {teamInfoToDisplay.name}
                    </Text>
                    <Text fontSize="sm" color={secondaryText} textAlign="center">
                      {level === 'MLB' ? 'Major League Roster' : 'AAA Roster'}
                    </Text>
                  </VStack>
                </HStack>
              </Box>

              {/* Contract Information */}
              <Box bg={hoverBg} p={4} borderRadius="md" border={`1px solid ${borderColor}`}>
                <Text fontSize="lg" fontWeight="semibold" mb={3} textAlign="center">
                  Current Contract
                </Text>
                <HStack spacing={6} justify="space-between">
                  <VStack align="center" spacing={2}>
                    <Text fontSize="sm" color={secondaryText} textAlign="center">
                      Current Salary
                    </Text>
                    <Text fontSize="xl" fontWeight="bold" color="green.500">
                      {formatSalary(currentYearSalary, true)}
                    </Text>
                  </VStack>
                  <VStack align="center" spacing={2}>
                    <Text fontSize="sm" color={secondaryText} textAlign="center">
                      Total Value
                    </Text>
                    <Text fontSize="xl" fontWeight="bold" color="blue.500">
                      {formatSalary(totalContractValue, true)}
                    </Text>
                  </VStack>
                  <VStack align="center" spacing={2}>
                    <Text fontSize="sm" color={secondaryText} textAlign="center">
                      Contract Years
                    </Text>
                    <Text fontSize="xl" fontWeight="bold" color="purple.500">
                      {remainingContractYears}
                    </Text>
                  </VStack>
                </HStack>
              </Box>

              {/* Bid Information */}
              {player.dealData && player.dealData.bids !== undefined && (
                <Box bg={hoverBg} p={3} borderRadius="md" border={`1px solid ${borderColor}`}>
                  <HStack align="flex-start" spacing={3}>
                    <Text fontSize="2xl" color="green.500" fontWeight="bold">
                      ✓
                    </Text>
                    <Text fontSize="sm" color={textColor}>
                      {player.dealData.bids > 0
                        ? `There are ${player.dealData.bids} offer${player.dealData.bids === 1 ? '' : 's'} for ${player.rwPlayer.last_name}.`
                        : `There are currently no offers for ${player.rwPlayer.last_name}.`
                      }
                      {player.dealData.expires && (
                        <>
                          <br />
                          Accepting offers until {formatPstDateTime(player.dealData.expires, true)}
                        </>
                      )}
                    </Text>
                  </HStack>
                </Box>
              )}

              {/* Your Bid Section */}
              <Box>
                <HStack justifyContent="space-between" alignItems="flex-start" mb={1}>
                  <Text fontWeight="medium" color={textColor} pt={1}>
                    Your Contract Offer
                  </Text>
                  <HStack spacing={2} alignItems="center">
                    <VStack alignItems="flex-end" spacing={0}>
                      {user?.systemSettings?.seasonStatus !== "In Season" && (
                        <Text fontSize="sm" color={secondaryText}>
                          Top Offer Score: {topOfferScore > 0 ? (topOfferScore / 1000000).toFixed(2) : '-'}
                        </Text>
                      )}
                      <Text fontSize="sm" color={(systemSettings?.seasonStatus !== 'In Season' && (yourOfferScore < topOfferScore)) ? "red.500" : secondaryText}>
                        Your Offer Score: {(yourOfferScore / 1000000).toFixed(2)}
                      </Text>
                    </VStack>
                    <Tooltip
                      label={
                        <HStack align="center" spacing={2} p={1} w='500px'>
                          <Text>Offer Score =</Text>
                          <VStack spacing={0} align="center">
                            <Text borderBottom="1px solid black" pb={1} px={1}>
                              (0.85 &times; AAV) + (0.15 &times; Total Contract Value)
                            </Text>
                            <Text pt={1}>1,000,000</Text>
                          </VStack>
                        </HStack>
                      }
                      fontSize="md"
                      p={2}
                      maxW="500px"
                      borderRadius="md"
                    >
                      <span><FaInfoCircle size={14} color="gray" /></span>
                    </Tooltip>
                  </HStack>
                </HStack>

                {displayedDealId && (
                  <Text fontSize="xs" color={secondaryText} mb={1} textAlign="center">
                    Deal ID: {displayedDealId}
                  </Text>
                )}

                {(existingDealId || displayedDealId) && submitterInfo && (
                  <Text fontSize="xs" color={secondaryText} mb={3} textAlign="center">
                    {submitterInfo.userName} submitted an offer on {submitterInfo.submittedDate}.
                  </Text>
                )}

                {/* Error message for invalid salaries */}
                {debouncedYearlySalaries.some((salary: number) => salary > 0 && salary < minSalary) && (
                  <Text fontSize="sm" color="red.500" textAlign="center" mb={3}>
                    Minimum annual salary for players is ${minSalary.toLocaleString()} (you entered ${debouncedYearlySalaries.find(s => s > 0 && s < minSalary)?.toLocaleString()})
                  </Text>
                )}

                {debouncedYearlySalaries.some((salary: number) => salary > maxSalary) && (
                  <Text fontSize="sm" color="red.500" textAlign="center" mb={3}>
                    Maximum annual salary for players is ${maxSalary.toLocaleString()} (you entered ${debouncedYearlySalaries.find(s => s > maxSalary)?.toLocaleString()})
                  </Text>
                )}

                {/* Error message for year-to-year salary changes */}
                {(() => {
                  for (let i = 1; i < debouncedYearlySalaries.length; i++) {
                    const currentSalary = debouncedYearlySalaries[i];
                    const previousSalary = debouncedYearlySalaries[i - 1];


                    // Only check if both years have valid salaries
                    if (currentSalary >= minSalary && previousSalary >= minSalary) {
                      const minAllowed = previousSalary * 0.5;
                      const maxAllowed = previousSalary * 2.0;

                      if (currentSalary < minAllowed || currentSalary > maxAllowed) {
                        return (
                          <Text fontSize="sm" color="red.500" textAlign="center" mb={3}>
                            Year-to-year salary changes must be between 50% and 200% of the previous year (min: ${minAllowed.toLocaleString()}, max: ${maxAllowed.toLocaleString()})
                          </Text>
                        );
                      }
                    }
                  }
                  return null;
                })()}

                {/* Error message for gaps */}
                {(() => {
                  let foundNonZero = false;
                  let hasGap = false;
                  for (let i = 0; i < debouncedYearlySalaries.length; i++) {
                    if (debouncedYearlySalaries[i] >= minSalary) {
                      foundNonZero = true;
                    } else if (debouncedYearlySalaries[i] === 0 && foundNonZero) {
                      // Found a zero after we've seen a non-zero - check if there are more non-zeros after
                      for (let j = i + 1; j < debouncedYearlySalaries.length; j++) {
                        if (debouncedYearlySalaries[j] >= minSalary) {
                          hasGap = true;
                          break;
                        }
                      }
                      if (hasGap) break;
                    }
                  }
                  return hasGap;
                })() && (
                    <Text fontSize="sm" color="red.500" textAlign="center" mb={3}>
                      Contract years must be consecutive without gaps
                    </Text>
                  )}

                <VStack spacing={3}>
                  {yearlySalaries.map((salary, index) => {
                    const showTrashIcon = index > 0 && index === yearlySalaries.length - 1;

                    // Check if this year has validation errors
                    const hasValidationError = (() => {
                      // Use debounced values for validation checking to delay error feedback
                      // Safely access debounced values (might be shorter if we just added a year)
                      const debouncedSalary = debouncedYearlySalaries[index];
                      // If debounced value is undefined or we haven't synced yet (simple check), skip validation
                      // This prevents early red text when adding/removing years rapidly
                      if (debouncedSalary === undefined) return false;

                      // First check: if this year has invalid salary range
                      if (debouncedSalary > 0 && (debouncedSalary < minSalary || debouncedSalary > maxSalary)) {
                        return true;
                      }

                      // Second check: if this year violates year-to-year salary change rules
                      // Check unsafe access for index-1
                      if (index > 0 && debouncedSalary >= minSalary && debouncedYearlySalaries[index - 1] !== undefined && debouncedYearlySalaries[index - 1] >= minSalary) {
                        const previousSalary = debouncedYearlySalaries[index - 1];
                        const minAllowed = previousSalary * 0.5;
                        const maxAllowed = previousSalary * 2.0;
                        if (salary < minAllowed || salary > maxAllowed) {
                          return true;
                        }
                      }

                      // Third check: if this year is part of a gap sequence
                      let foundNonZero = false;
                      for (let i = 0; i < debouncedYearlySalaries.length; i++) {
                        if (debouncedYearlySalaries[i] >= minSalary) {
                          foundNonZero = true;
                        } else if (debouncedYearlySalaries[i] === 0 && foundNonZero) {
                          // Found a zero after we've seen a non-zero - check if there are more non-zeros after
                          for (let j = i + 1; j < debouncedYearlySalaries.length; j++) {
                            if (debouncedYearlySalaries[j] >= minSalary) {
                              // This is a gap - mark all years in the gap sequence
                              if (i <= index && index <= j) {
                                return true;
                              }
                            }
                          }
                        }
                      }
                      return false;
                    })();

                    return (
                      <HStack key={index} spacing={4} justify="space-between" w="full">
                        <Text
                          fontSize="sm"
                          color={hasValidationError ? "red.500" : secondaryText}
                          minW="80px"
                          fontWeight={hasValidationError ? "bold" : "normal"}
                        >
                          {index === 0 ? 'This Year' : String(currentYear + index)}:
                        </Text>
                        <HStack spacing={2} flex={1}>
                          <InputGroup size="sm" flex={1}>
                            <NumberInput
                              value={salary}
                              onChange={(value) => handleSalaryChange(index, value)}
                              min={0}
                              size="sm"
                              w="full"
                              format={(value) => value ? `$${Number(value).toLocaleString()}` : ''}
                              parse={(value) => value.replace(/,/g, '').replace(/\$/g, '')}
                              isReadOnly={viewOnly}
                            >
                              <NumberInputField
                                bg={bgColor}
                                color={textColor}
                                borderColor={hasValidationError ? 'red.500' : borderColor}
                                _placeholder={{ color: secondaryText }}
                                placeholder="0"
                                pr="2rem"
                              />
                            </NumberInput>
                            {salary > 0 && !viewOnly && (
                              <InputRightElement width="2rem">
                                <IconButton
                                  size="xs"
                                  variant="ghost"
                                  icon={<FaTimes />}
                                  onClick={() => handleSalaryChange(index, "0")}
                                  aria-label="Clear value"
                                  color={secondaryText}
                                  _hover={{ bg: 'transparent', color: 'red.500' }}
                                />
                              </InputRightElement>
                            )}
                          </InputGroup>
                          {!viewOnly && existingDealId && (
                            <IconButton
                              size="sm"
                              variant="ghost"
                              icon={<FaUndo />}
                              onClick={() => {
                                // Revert to initial value if it exists, otherwise set to 0
                                const newYearlySalaries = [...yearlySalaries];
                                newYearlySalaries[index] = (initialYearlySalaries && initialYearlySalaries[index]) || 0;
                                setYearlySalaries(newYearlySalaries);
                              }}
                              aria-label={`Undo changes for ${index === 0 ? 'this year' : String(currentYear + index)}`}
                              minW="auto"
                              px={2}
                              color={secondaryText}
                            />
                          )}
                          {!viewOnly && showTrashIcon && (
                            <IconButton
                              size="sm"
                              variant="ghost"
                              colorScheme="red"
                              icon={<FaTrash />}
                              onClick={() => handleRemoveLastYear()}
                              aria-label="Remove this year"
                              minW="auto"
                              px={2}
                            />
                          )}
                        </HStack>
                      </HStack>
                    );
                  })}

                  {/* Add Year Button - Hide in viewOnly mode */}
                  {!viewOnly && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleAddYear}
                      isDisabled={!canAddYear}
                      w="full"
                    >
                      {yearlySalaries.length >= maxYears ? "Maximum seven years allowed" : "+ Add Year"}
                    </Button>
                  )}

                  <Divider borderColor={borderColor} />
                  <HStack spacing={4} justify="space-between" w="full">
                    <Text fontSize="md" fontWeight="semibold" color={textColor}>
                      Total Bid:
                    </Text>
                    <Text fontSize="lg" fontWeight="bold" color={accentColor}>
                      {formatSalary(totalBid, true)}
                    </Text>
                  </HStack>
                </VStack>
              </Box>

              {/* Value Too Low Warning */}
              {isValueTooLow && (
                <Alert status="error" borderRadius="md">
                  <AlertIcon />
                  <Box>
                    <AlertTitle>Offer Value Too Low</AlertTitle>
                    <AlertDescription>
                      When updating an offer, the total value must be at least {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(initialTotalValue)}.
                    </AlertDescription>
                  </Box>
                </Alert>
              )}

              {/* Error Display */}
              {error && (
                <Alert status="error" borderRadius="md">
                  <AlertIcon />
                  <Box>
                    <AlertTitle>Error!</AlertTitle>
                    <AlertDescription>{error}</AlertDescription>
                  </Box>
                </Alert>
              )}
            </VStack>
          )}
        </ModalBody>

        <ModalFooter>
          <HStack spacing={3}>
            {viewOnly ? (
              <Button
                colorScheme="blue"
                onClick={handleClose}
                w="100px"
              >
                Close
              </Button>
            ) : (
              <>
                <Button variant="outline" onClick={handleClose} isDisabled={isSubmitting}>
                  Cancel
                </Button>
                <Button
                  colorScheme="blue"
                  onClick={handleSignPlayer}
                  isLoading={isSubmitting}
                  loadingText="Submitting..."
                  leftIcon={<FaDollarSign />}
                  disabled={isSubmitting || !isFormValid || (systemSettings?.seasonStatus !== 'In Season' && yourOfferScore < topOfferScore)}
                  w="200px"
                >
                  {existingDealId ? 'Update Offer' : 'Submit Offer'}
                </Button>
              </>
            )}
          </HStack>
        </ModalFooter>
      </ModalContent>

      {/* Stripe Pre-Approval Modal for Salary Cap Exceedance */}
      <StripePaymentForm
        isOpen={isPreApprovalOpen}
        onClose={handlePreApprovalClose}
        amount={preApprovalAmount}
        payload={{
          type: 'free_agent_offer_salary_cap_exceedance',
          playerId: player.id || undefined,
          teamId: myTeam || undefined,
          leagueId: myLeague?.id || undefined,
        }}
        onSuccess={handlePreApprovalSuccess}
      />
    </Modal>
  );
};

export default SignFreeAgentModal; 